import { gsap } from "gsap";

export function initMaximCard() {
  const maximCards = document.querySelectorAll(".js-maxim-card");

  if (maximCards.length === 0) return;

  maximCards.forEach((maximCard) => {
    setupMaximCard(maximCard);
  });
}

function setupMaximCard(maximCard) {
  const start = maximCard.querySelector(".js-maxim-card-start");
  const button = maximCard.querySelector(".js-maxim-card-button");
  const result = maximCard.querySelector(".js-maxim-card-result");
  const resultImg = maximCard.querySelector(".js-maxim-card-result-img");
  const backWrap = maximCard.querySelector(".p-maxim-card__back-wrap");
  const backButton = maximCard.querySelector(".js-maxim-card-back");
  const cardItems = [...maximCard.querySelectorAll(".js-maxim-card-list [data-card-src]")];

  const bgLayers = [
    maximCard.querySelector(".p-maxim-card__bg-layer--01"),
    maximCard.querySelector(".p-maxim-card__bg-layer--02"),
    maximCard.querySelector(".p-maxim-card__bg-layer--03"),
    maximCard.querySelector(".p-maxim-card__bg-layer--04"),
    maximCard.querySelector(".p-maxim-card__bg-layer--05"),
  ];

  const [bg01, bg02, bg03, bg04, bg05] = bgLayers;

  if (
    !start ||
    !button ||
    !result ||
    !resultImg ||
    !backWrap ||
    !backButton ||
    cardItems.length === 0 ||
    bgLayers.some((bgLayer) => !bgLayer)
  ) {
    return;
  }

  let isAnimating = false;
  let previousCardIndex = -1;

  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const backgroundPreloadPromise = preloadBackgroundImages(bgLayers);

  setInitialState();

  button.addEventListener("click", async () => {
    if (isAnimating) return;

    isAnimating = true;
    button.disabled = true;
    backButton.disabled = true;
    maximCard.setAttribute("aria-busy", "true");

    const selectedIndex = getRandomCardIndex(cardItems.length, previousCardIndex);
    const selectedCard = cardItems[selectedIndex];
    const selectedSrc = selectedCard.dataset.cardSrc;
    const selectedAlt = selectedCard.dataset.cardAlt || "本日の格言カード";

    previousCardIndex = selectedIndex;

    resultImg.src = selectedSrc;
    resultImg.alt = selectedAlt;

    await Promise.all([backgroundPreloadPromise, preloadImage(selectedSrc)]);

    if (mediaQuery.matches) {
      showResultWithoutAnimation();
      return;
    }

    playResultAnimation();
  });

  backButton.addEventListener("click", () => {
    if (isAnimating) return;

    isAnimating = true;
    backButton.disabled = true;
    maximCard.setAttribute("aria-busy", "true");

    if (mediaQuery.matches) {
      resetWithoutAnimation();
      return;
    }

    playResetAnimation();
  });

  function setInitialState() {
    result.hidden = true;

    gsap.set(bg01, {
      autoAlpha: 1,
      scale: 1,
    });

    gsap.set([bg02, bg03, bg04, bg05], {
      autoAlpha: 0,
      scale: 1.04,
    });

    gsap.set(start, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      pointerEvents: "auto",
    });

    gsap.set(result, {
      autoAlpha: 0,
      y: 0,
      scale: 1,
      pointerEvents: "none",
    });

    gsap.set(resultImg, {
      autoAlpha: 0,
      y: 22,
      scale: 0.98,
      rotate: 0,
    });

    gsap.set(backWrap, {
      autoAlpha: 0,
      y: 10,
      pointerEvents: "none",
    });

    button.disabled = false;
    backButton.disabled = false;
    maximCard.removeAttribute("aria-busy");
    isAnimating = false;
  }

  function playResultAnimation() {
    result.hidden = false;

    gsap.set(resultImg, {
      autoAlpha: 0,
      y: 22,
      scale: 0.98,
      rotate: 0,
    });

    gsap.set(backWrap, {
      autoAlpha: 0,
      y: 10,
      pointerEvents: "none",
    });

    const timeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
      },
      onComplete: () => {
        button.disabled = false;
        backButton.disabled = false;
        maximCard.setAttribute("aria-busy", "false");
        backButton.focus();
        isAnimating = false;
      },
    });

    timeline
      .to(button, {
        scale: 0.985,
        duration: 0.18,
        ease: "power2.out",
      })
      .to(button, {
        scale: 1,
        duration: 0.24,
        ease: "power2.out",
      })
      .to(
        start,
        {
          autoAlpha: 0,
          y: -10,
          scale: 0.995,
          duration: 0.45,
          ease: "power2.inOut",
          pointerEvents: "none",
        },
        "+=0.05"
      )
      .to(
        bg02,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.62,
          ease: "power2.inOut",
        },
        "-=0.12"
      )
      .to(
        bg03,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.42,
          ease: "power3.out",
        },
        "-=0.04"
      )
      .to(
        bg04,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.34,
          ease: "power4.out",
        },
        "-=0.06"
      )
      .to(
        bg04,
        {
          scale: 1.035,
          duration: 0.58,
          ease: "power2.inOut",
        },
        "+=0.08"
      )
      .to(
        bg05,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.78,
          ease: "power2.inOut",
        },
        "-=0.08"
      )
      .to(
        result,
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.42,
          ease: "power2.out",
          pointerEvents: "auto",
        },
        "-=0.22"
      )
      .to(
        resultImg,
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.68,
          ease: "power3.out",
        },
        "-=0.12"
      )
      .to(
        backWrap,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.38,
          ease: "power2.out",
          pointerEvents: "auto",
        },
        "-=0.08"
      );
  }

  function playResetAnimation() {
    const timeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
      },
      onComplete: () => {
        result.hidden = true;
        resultImg.removeAttribute("src");
        resultImg.alt = "";
        setInitialState();
        button.focus();
      },
    });

    timeline
      .to(backWrap, {
        autoAlpha: 0,
        y: 10,
        duration: 0.18,
        pointerEvents: "none",
      })
      .to(
        resultImg,
        {
          autoAlpha: 0,
          y: 12,
          scale: 0.98,
          duration: 0.24,
        },
        "<"
      )
      .to(
        result,
        {
          autoAlpha: 0,
          y: 16,
          scale: 0.98,
          duration: 0.25,
          pointerEvents: "none",
        },
        "<"
      )
      .to(
        bg01,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.45,
        },
        "-=0.05"
      )
      .to(
        [bg02, bg03, bg04, bg05],
        {
          autoAlpha: 0,
          scale: 1.04,
          duration: 0.45,
        },
        "<"
      )
      .to(
        start,
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.35,
          pointerEvents: "auto",
        },
        "-=0.2"
      );
  }

  function showResultWithoutAnimation() {
    result.hidden = false;

    gsap.set(start, {
      autoAlpha: 0,
      pointerEvents: "none",
    });

    gsap.set([bg01, bg02, bg03, bg04], {
      autoAlpha: 0,
    });

    gsap.set(bg05, {
      autoAlpha: 1,
      scale: 1,
    });

    gsap.set(result, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      pointerEvents: "auto",
    });

    gsap.set(resultImg, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      rotate: 0,
    });

    gsap.set(backWrap, {
      autoAlpha: 1,
      y: 0,
      pointerEvents: "auto",
    });

    button.disabled = false;
    backButton.disabled = false;
    maximCard.setAttribute("aria-busy", "false");
    backButton.focus();
    isAnimating = false;
  }

  function resetWithoutAnimation() {
    result.hidden = true;
    resultImg.removeAttribute("src");
    resultImg.alt = "";
    setInitialState();
    button.focus();
  }
}

function getRandomCardIndex(cardLength, previousCardIndex) {
  if (cardLength <= 1) return 0;

  let randomIndex = Math.floor(Math.random() * cardLength);

  while (randomIndex === previousCardIndex) {
    randomIndex = Math.floor(Math.random() * cardLength);
  }

  return randomIndex;
}

function preloadBackgroundImages(bgLayers) {
  const imageUrls = bgLayers
    .map((bgLayer) => getBackgroundImageUrl(bgLayer))
    .filter(Boolean);

  return Promise.all(imageUrls.map((url) => preloadImage(url)));
}

function getBackgroundImageUrl(element) {
  const backgroundImage = window.getComputedStyle(element).backgroundImage;
  const match = backgroundImage.match(/url\(["']?(.+?)["']?\)/);

  return match ? match[1] : "";
}

function preloadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }

    const image = new Image();

    image.onload = resolve;
    image.onerror = resolve;
    image.src = src;
  });
}